import { useTranslation } from "react-i18next"

import { StatusBadge } from "@/components/status-badge"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { IntakeLoad } from "@/features/intake/api/types"
import { QualityMathDialog } from "@/features/intake/components/quality-math"
import { gradeTone } from "@/features/intake/lib/intake-status"
import { computeRefractionKg } from "@/features/intake/lib/quality-rules"
import { cn } from "@/lib/utils"
import { formatNumber } from "@/lib/format"
// `formatKg` was a thin wrapper around formatNumber that forced a
// comma decimal separator. formatNumber now follows the active i18n
// locale, so we just call it directly.

interface BordereauBreakdownProps {
  load: IntakeLoad
}

interface LineProps {
  label: string
  value: string
  // When provided, the label shows a dotted underline and reveals this
  // node in a tooltip on hover/focus. Lets every computed line in the
  // bordereau be auditable without inline noise.
  derivation?: React.ReactNode
  tone?: "deduct"
  bold?: boolean
}

function Line({ label, value, derivation, tone, bold }: LineProps) {
  const labelEl = derivation ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "cursor-help border-b border-dotted border-muted-foreground/40 text-left",
            bold ? "font-medium" : "text-muted-foreground",
          )}
        >
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" align="start" className="max-w-xs">
        <div className="font-mono text-[11px] tabular-nums leading-relaxed">
          {derivation}
        </div>
      </TooltipContent>
    </Tooltip>
  ) : (
    <span className={bold ? "font-medium" : "text-muted-foreground"}>
      {label}
    </span>
  )

  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
      {labelEl}
      <span
        className={
          "tabular-nums " +
          (tone === "deduct"
            ? "text-red-600 dark:text-red-400"
            : bold
              ? "font-semibold"
              : "")
        }
      >
        {value}
      </span>
    </div>
  )
}

const WET_BAG_KG = 65
const DEFAULT_PRICE = 1500

// Bordereau formatting now follows the active i18n locale: French
// shows "26 799" / "133,35"; English shows "26,799" / "133.35".
// Callers pass `decimals` explicitly per line — kg weights use 2,
// FCFA totals use 0.
function formatKg(n: number, decimals = 0): string {
  return formatNumber(n, decimals)
}

export function BordereauBreakdown({ load }: BordereauBreakdownProps) {
  const { t } = useTranslation("intake")

  const gross = load.grossWeight ?? 0
  const tare = load.tareWeight ?? 0
  const bagsUnloaded = load.bagsUnloaded ?? 0
  const bagsTareKg = load.bagsTareKg ?? bagsUnloaded * 0.67
  const net = load.netWeight ?? Math.max(0, gross - tare - bagsTareKg)

  const bagsWet = load.sampling?.bagsWet ?? 0
  const wet = load.wetBagDeductionKg ?? bagsWet * WET_BAG_KG
  const accepted = load.acceptedWeightKg ?? Math.max(0, net - wet)

  // Lab refraction: prefer the BE-stamped `labRefractionKg` (only set on
  // COMPLETED loads — already rounded to whole kg by ComputeBordereau).
  // Otherwise re-derive raw kg (no rounding here so the display keeps two
  // decimals like 133,35; the BE rounds at decision time, not preview).
  //
  // Reading `load.totalRefractionKg` directly is wrong: the BE writes it
  // at lab-save time against the bagsWet it knew *then*. If sampling
  // changed afterwards, accepted weight shifts and the stored kg goes
  // stale until the next save round-trip. Recomputing on the FE keeps
  // the bordereau and live panel in sync.
  const lab = (() => {
    if (load.labRefractionKg != null) return load.labRefractionKg
    const lr = load.labResults
    if (!lr || accepted <= 0) return load.totalRefractionKg ?? 0
    const refr = computeRefractionKg(
      {
        moisture: lr.moisture,
        foreignMatter: lr.foreignMatter,
        broken: lr.broken,
        waste: lr.waste,
        crabot: lr.crabot,
        mouldy: lr.mouldy,
      },
      accepted,
    )
    if (refr.rejected) return 0
    return refr.totalKg
  })()
  const netAfter = load.netAfterRefractionKg ?? Math.max(0, accepted - lab)

  const price = load.priceFcfaPerKg ?? load.Orders?.basePrice ?? DEFAULT_PRICE
  const amount = load.amountFcfa ?? Math.round(netAfter * price)

  return (
    <div className="rounded-md border p-4">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-muted-foreground text-[11px] uppercase tracking-wide">
            {t("decision.netAfterRefraction")}
          </div>
          <div className="text-2xl font-semibold tabular-nums">
            {formatKg(netAfter, 2)} kg
          </div>
        </div>
        {load.grade ? (
          <div className="text-right">
            <div className="text-muted-foreground text-[11px] uppercase tracking-wide">
              {t("decision.gradeCcc")}
            </div>
            <StatusBadge tone={gradeTone(load.grade)}>
              {t(`grade.${load.grade}`)}
            </StatusBadge>
          </div>
        ) : null}
      </div>

      <div className="my-3 border-t" />

      <div className="text-muted-foreground mb-2 text-[10px] uppercase tracking-wide">
        {t("decision.calcHeader")}
      </div>

      <Line
        label={t("decision.lines.net")}
        value={`${formatKg(net, 2)} kg`}
        derivation={
          gross > 0 ? (
            <>
              {formatKg(gross, 2)} − {formatKg(tare, 2)} − {formatKg(bagsTareKg, 2)}
              <div className="mt-1 text-[10px] opacity-70">
                {t("decision.derivations.bagsTareHint", {
                  bags: bagsUnloaded,
                  rate: formatNumber(0.67, 2),
                })}
              </div>
            </>
          ) : undefined
        }
      />
      <Line
        label={t("decision.lines.wet")}
        value={`−${formatKg(wet, 2)} kg`}
        derivation={
          bagsWet > 0 ? (
            <>
              {bagsWet} × {WET_BAG_KG} kg
              <div className="mt-1 text-[10px] opacity-70">
                {t("decision.derivations.wetBagsHint", { count: bagsWet })}
              </div>
            </>
          ) : (
            <>{t("decision.derivations.noWetBags")}</>
          )
        }
        tone="deduct"
      />
      <Line
        label={t("decision.lines.accepted")}
        value={`${formatKg(accepted, 2)} kg`}
        derivation={
          <>
            {formatKg(net, 2)} − {formatKg(wet, 2)}
          </>
        }
      />
      <LabRefractionLine
        label={t("decision.lines.lab")}
        value={`−${formatKg(lab, 2)} kg`}
        load={load}
        accepted={accepted}
      />
      <div className="border-border/60 my-2 border-t border-dashed" />
      <Line
        label={t("decision.lines.netAfter")}
        value={`${formatKg(netAfter, 2)} kg`}
        derivation={
          <>
            {formatKg(accepted, 2)} − {formatKg(lab, 2)}
          </>
        }
        bold
      />
      <Line
        label={t("decision.lines.price")}
        value={`${formatKg(price)} XOF / kg`}
      />
      <Line
        label={t("decision.lines.amount")}
        value={`${formatKg(amount)} XOF`}
        derivation={
          <>
            {formatKg(netAfter, 2)} × {formatKg(price)} XOF
          </>
        }
        bold
      />
    </div>
  )
}

interface LabRefractionLineProps {
  label: string
  value: string
  load: IntakeLoad
  accepted: number
}

// LabRefractionLine renders the bordereau's lab-refraction row.
// Clicking the label (when lab analysis is saved) opens the wide
// QualityMathDialog so the operator can audit every metric, exactly the
// same dialog reachable from the live grade panel. When lab isn't saved
// yet, the label degrades to plain text — there's nothing to drill into.
function LabRefractionLine({
  label,
  value,
  load,
  accepted,
}: LabRefractionLineProps) {
  const { t } = useTranslation("intake")
  const lr = load.labResults

  const valueEl = (
    <span className="tabular-nums text-red-600 dark:text-red-400">{value}</span>
  )

  if (!lr) {
    return (
      <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
        <span className="text-muted-foreground">{label}</span>
        {valueEl}
      </div>
    )
  }

  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
      <QualityMathDialog
        refraction={{
          moisture: lr.moisture,
          foreignMatter: lr.foreignMatter,
          broken: lr.broken,
          waste: lr.waste,
          crabot: lr.crabot,
          mouldy: lr.mouldy,
        }}
        classification={{
          mouldy: lr.mouldy,
          slaty: lr.slaty,
          germinated: lr.germinated,
          mites: lr.mites,
          flatBeans: lr.flatBeans,
        }}
        acceptedKg={accepted}
        trigger={
          <button
            type="button"
            className="cursor-pointer border-b border-dotted border-muted-foreground/40 text-left text-muted-foreground hover:text-foreground"
            aria-label={t("decision.derivations.labDetail")}
          >
            {label}
          </button>
        }
      />
      {valueEl}
    </div>
  )
}
